import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import axios from "../../../axios";
import dayjs from "dayjs";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Card, CardContent } from "../../../components/ui/card";
import { toast } from "react-toastify";
import { getAllowedBranchIds } from "../../../utils/tokenUtils";

// ✅ التصدير
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function SalesItemsSummary() {
  const { t } = useTranslation();
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const defaultBranchId = parseInt(localStorage.getItem("branchId"), 10);

  const userId = parseInt(localStorage.getItem('userId'), 10);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    dateFrom: dayjs().startOf("month").format("YYYY-MM-DD"),
    dateTo: dayjs().endOf("month").format("YYYY-MM-DD"),
    stockId: "",
    productId: "",
    branchId: "",
  });

  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data));
    axios.get(`/Product/by-company/${companyId}`).then(r => setProducts(r.data));
    
    // جلب الفروع وتصفيتها حسب الفروع المسموحة
    axios.get(`/Branch/by-company/${companyId}`).then(r => {
      const allowedBranchIds = getAllowedBranchIds();
      // ✅ القاعدة: إذا كانت القائمة فارغة → المستخدم له صلاحية على كل الفروع
      if (allowedBranchIds && allowedBranchIds.length > 0) {
        const filteredBranches = r.data.filter(b => allowedBranchIds.includes(b.id));
        setBranches(filteredBranches);
        if (defaultBranchId > 0 && filteredBranches.some(b => b.id === defaultBranchId)) {
          setFilters(prev => ({ ...prev, branchId: defaultBranchId.toString() }));
        } else if (filteredBranches.length > 0) {
          setFilters(prev => ({ ...prev, branchId: filteredBranches[0].id.toString() }));
        }
      } else {
        // المستخدم له صلاحية على كل الفروع → عرض الكل
        setBranches(r.data);
      }
    });
  }, [companyId, defaultBranchId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchReport = async () => {
    if (!companyId) {
      toast.error(t("common.companyIdRequired"));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get("/Invoice/SalesItemsSummary", {
        params: {
          companyId,
          branchId: filters.branchId ? Number(filters.branchId) : 0,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          stockId: filters.stockId || null,
          productId: filters.productId || null,
        },
      });
      setRows(res.data);
    } catch (err) {
      toast.error(t("invoicesReport.errorFetchingReport"));
    } finally {
      setLoading(false);
    }
  };

  // ✅ التصدير إلى Excel
  const exportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SalesItemsSummary");
    XLSX.writeFile(workbook, `SalesItemsSummary_${filters.dateFrom}_${filters.dateTo}.xlsx`);
  };

  // ✅ التصدير إلى PDF
  const exportPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    doc.text(t("salesMenu.aggregatedItemsSalesReport"), 40, 30);

    const tableColumn = [
      t("invoice.fields.ProductId"),
      t("invoice.fields.Quantity"),
      t("invoicesReport.columns.total"),
      t("invoicesReport.columns.discount"),
      t("invoicesReport.columns.tax"),
      t("invoicesReport.columns.net"),
    ];

    const tableRows = rows.map((r) => [
      r.productNameAr,
      r.stockName,
      r.totalQuantity,
      r.grossSales.toFixed(2),
      r.totalDiscount.toFixed(2),
      r.totalVAT.toFixed(2),
      r.netSales.toFixed(2),
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 50,
    });

    doc.save(`SalesItemsSummary_${filters.dateFrom}_${filters.dateTo}.pdf`);
  };

  const columns = [
    { field: "productId", headerName: "ID", width: 70 },
    { field: "productNameAr", headerName: t("invoice.fields.ProductId"), flex: 1 },
    { field: "totalQuantity", headerName: t("invoice.fields.Quantity"), type: "number", flex: 1 },
    { field: "grossSales", headerName: t("invoicesReport.columns.total"), type: "number", flex: 1 },
    { field: "totalDiscount", headerName: t("invoicesReport.columns.discount"), type: "number", flex: 1 },
    { field: "totalVAT", headerName: t("invoicesReport.columns.tax"), type: "number", flex: 1 },
    { field: "netSales", headerName: t("invoicesReport.columns.net"), type: "number", flex: 1 },
  ];

  const grandTotal = rows.reduce((sum, r) => sum + (r.netSales || 0), 0);
  const TotalQty = rows.reduce((sum, r) => sum + (r.totalQuantity || 0), 0);
  const TotalSales = rows.reduce((sum, r) => sum + (r.grossSales || 0), 0);
  const totalsVAT = rows.reduce((sum, r) => sum + (r.totalVAT || 0), 0);
  const TotalDiscount = rows.reduce((sum, r) => sum + (r.totalDiscount || 0), 0);
  return (
    <Card>
      <CardContent>
        <h2 className="text-xl font-bold mb-4">{t("salesMenu.aggregatedItemsSalesReport")}</h2>

        {/* فلاتر */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-4">
          <div>
            <label>{t("ledgerReport.fromDate")}</label>
            <Input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleChange} />
          </div>
          <div>
            <label>{t("ledgerReport.toDate")}</label>
            <Input type="date" name="dateTo" value={filters.dateTo} onChange={handleChange} />
          </div>
          <div>
            <label>{t("ledgerReport.branch", "الفرع")}</label>
            <select
              name="branchId"
              value={filters.branchId}
              onChange={handleChange}
              className="w-full border p-2 rounded"
            >
              <option value="">{t("common.all")}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nameAr || b.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>{t("warehousesMenu.addWarehouse")}</label>
            <select
              name="stockId"
              value={filters.stockId}
              onChange={handleChange}
              className="w-full border p-2 rounded"
            >
              <option value="">{t("common.all")}</option>
              {stocks.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nameAr}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>{t("invoice.fields.ProductId")}</label>
            <select
              name="productId"
              value={filters.productId}
              onChange={handleChange}
              className="w-full border p-2 rounded"
            >
              <option value="">{t("common.all")}</option>
              {products.map((p) => (
                <option key={p.productId} value={p.productId}>
                  {p.arabicName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={fetchReport}>{t("invoicesReport.fetchInvoices")}</Button>
            <Button onClick={exportExcel} >
              Excel
            </Button>
            <Button onClick={exportPDF} >
              PDF
            </Button>
          </div>
        </div>

        {/* جدول */}
        <div style={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={rows.map((r, idx) => ({ id: idx + 1, ...r }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading}
          />
        </div>
              {/* الإجمالي */}
<div className="mt-4 p-3 bg-blue-50 border rounded font-bold text-lg flex justify-between items-center text-right">
  
  <div>
    {t("invoice.summary.TotalQty")}:{" "}
    <span className="text-green-700">
      {TotalQty.toFixed(2)}
    </span>
  </div>

  <div>
    {t("invoice.summary.TotalSales")}:{" "}
    <span className="text-green-700">
      {TotalSales.toFixed(2)}
    </span>
  </div>
  <div>
    {t("invoice.summary.TotalDiscount")}:{" "}
    <span className="text-green-700">
      {TotalDiscount.toFixed(2)}
    </span>
  </div>
  <div>
    {t("invoice.summary.totalsVAT")}:{" "}
    <span className="text-green-700">
      {totalsVAT.toFixed(2)}
    </span>
  </div>
  <div>
    {t("invoice.summary.net")}:{" "}
    <span className="text-green-700">
      {grandTotal.toFixed(2)}
    </span>
  </div>
  </div>

      </CardContent>
    </Card>
  );
}
